import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FloorplanCanvas, type StatusLevel } from '@/lib';
import { goAsset } from '@/app/navigation';
import { useApiClients } from '@/app/ApiProvider';
import { useSites } from './queries';

export type FleetMapAsset = {
  assetId: string;
  name: string;
  siteName: string;
  currentStatus: StatusLevel;
};

export type FleetMapProps = {
  assets: FleetMapAsset[];
};

// Deterministic pseudo-random pair in (0.1 .. 0.9) so an asset always
// renders at the same spot until real coordinates land. Used as the
// fallback when nothing in the API surface carries (x, y) yet.
function syntheticPosition(seed: string): { x: number; y: number } {
  let s = 0;
  for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) >>> 0;
  const x = 0.1 + ((s % 800) / 1000);
  s = (s * 9301 + 49297) % 233280;
  const y = 0.1 + ((s % 800) / 1000);
  return { x, y };
}

/**
 * Floorplan-first Fleet Map: groups assets by site name (or site id
 * when the asset carries one), renders one FloorplanCanvas per site.
 * Falls back to a "no sites yet" empty state when the sites resource
 * is empty. When real geo lands we can swap each canvas for MapLibre
 * without touching this grouping logic.
 */
export function FleetMap({ assets }: FleetMapProps) {
  const navigate = useNavigate();
  const clients = useApiClients();
  const sitesQ = useSites(clients);

  const groups = useMemo(() => {
    const byName = new Map<string, FleetMapAsset[]>();
    for (const a of assets) {
      const key = a.siteName || 'Unassigned';
      if (!byName.has(key)) byName.set(key, []);
      byName.get(key)!.push(a);
    }
    return [...byName.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [assets]);

  // Index the sites resource by name so we can wire floorplan URLs
  // when present. Falls back to a synthetic site shape when the
  // resource has no row for a given assetSiteName yet (typical in
  // dev where assets ship with a free-text siteName).
  const siteByName = useMemo(() => {
    const out = new Map<string, { id: string; name: string; floorplanUrl?: string }>();
    (sitesQ.data ?? []).forEach((s) =>
      out.set(s.name, { id: s.id, name: s.name, floorplanUrl: s.floorplan_url }),
    );
    return out;
  }, [sitesQ.data]);

  return (
    <div className="flex flex-col gap-sp-6">
      {groups.length === 0 ? (
        <p className="rounded-r-card border border-gray-300 bg-gray-50 px-sp-4 py-sp-3 text-fs-meta text-gray-700">
          No assets to render on the map.
        </p>
      ) : null}
      {groups.map(([siteName, siteAssets]) => {
        const site = siteByName.get(siteName) ?? {
          id: `synthetic-${siteName.replace(/\s+/g, '-').toLowerCase()}`,
          name: siteName,
        };
        const glyphs = siteAssets.map((a) => ({
          id: a.assetId,
          name: a.name,
          severity: a.currentStatus,
          ...syntheticPosition(a.assetId),
        }));
        return (
          <details key={site.id} open className="[&_summary]:cursor-pointer">
            <summary className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
              {site.name}
            </summary>
            <div className="mt-sp-3">
              <FloorplanCanvas
                site={site}
                assets={glyphs}
                onSelect={(id) => goAsset(navigate, id)}
              />
            </div>
          </details>
        );
      })}
      <SeverityLegend />
    </div>
  );
}

const LEGEND: Array<{ level: StatusLevel; label: string }> = [
  { level: 'critical', label: 'Critical' },
  { level: 'warn', label: 'Warn' },
  { level: 'watch', label: 'Watch' },
  { level: 'healthy', label: 'Healthy' },
];

function SeverityLegend() {
  return (
    <ul className="flex flex-wrap items-center gap-sp-3 text-fs-meta text-gray-700">
      {LEGEND.map((l) => (
        <li key={l.level} className="inline-flex items-center gap-sp-2">
          <span
            aria-hidden
            className="inline-block h-3 w-3 rounded-full border border-gray-900"
            style={{ background: `var(--color-${l.level})` }}
          />
          <span>{l.label}</span>
        </li>
      ))}
    </ul>
  );
}
